import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  alpha,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Router as RouterIcon,
  Search as SearchIcon,
  ExitToApp as ExitToAppIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import equipmentService from '../../services/equipmentService';
import { Equipment } from '../../types';

interface LancarSaidaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LancarSaidaModal: React.FC<LancarSaidaModalProps> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const today = new Date().toISOString().split('T')[0];

  const [dataRegistro, setDataRegistro] = useState(today);
  const [funcionarioId, setFuncionarioId] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [equipamentosLivres, setEquipamentosLivres] = useState<Equipment[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEquipamentosLivres = useCallback(async () => {
    try {
      setLoading(true);
      const response = await equipmentService.getEquipments({
        status_uso: 'livre',
        limit: 500,
        search: searchTerm || undefined,
      });

      if (response.success && response.data) {
        const data = response.data as any;
        const list = Array.isArray(data) ? data : data.equipments || [];
        setEquipamentosLivres(list);
      } else {
        setEquipamentosLivres([]);
      }
    } catch (err) {
      console.error('Erro ao carregar equipamentos livres:', err);
      setEquipamentosLivres([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (open) {
      loadEquipamentosLivres();
    }
  }, [open, loadEquipamentosLivres]);

  useEffect(() => {
    if (!open) {
      setDataRegistro(today);
      setFuncionarioId(null);
      setObservacoes('');
      setSearchTerm('');
      setSelectedIds(new Set());
      setError(null);
    }
  }, [open, today]);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === equipamentosLivres.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(equipamentosLivres.map((e) => e.id)));
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!dataRegistro) { setError('Informe a data de saída'); return; }
    if (!funcionarioId) { setError('Selecione o funcionário'); return; }
    if (selectedIds.size === 0) { setError('Selecione ao menos um equipamento'); return; }

    try {
      setSubmitting(true);
      const response = await equipmentService.lancarSaida({
        data_registro: dataRegistro,
        funcionario_id: funcionarioId,
        equipamento_ids: Array.from(selectedIds),
        observacoes: observacoes || undefined,
      });

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setError((response as any).message || 'Erro ao lançar saída');
      }
    } catch (err: any) {
      console.error('Erro ao lançar saída:', err);
      setError(err?.response?.data?.message || err?.message || 'Erro ao lançar saída');
    } finally {
      setSubmitting(false);
    }
  };

  const primario = theme.palette.primary.main;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ExitToAppIcon color="primary" />
        Lançar Saída de Equipamentos
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Data de Saída"
            type="date"
            value={dataRegistro}
            onChange={(e) => setDataRegistro(e.target.value)}
            size="small"
            required
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <FuncionariosSelect
              value={funcionarioId}
              onChange={setFuncionarioId}
              label="Funcionário / Técnico"
              placeholder="Selecione o funcionário"
              size="small"
              required
              allowClear={false}
            />
          </Box>
        </Box>

        <TextField
          label="Observações"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Equipamentos Livres ({equipamentosLivres.length})
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedIds.size > 0 && (
              <Chip label={`${selectedIds.size} selecionado(s)`} color="primary" size="small" onDelete={() => setSelectedIds(new Set())} />
            )}
            <TextField
              size="small"
              placeholder="Buscar equipamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 250 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>
          ) : equipamentosLivres.length === 0 ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography color="text.secondary">Nenhum equipamento livre encontrado</Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`, fontWeight: 700, fontSize: '0.73rem', py: 0.8 } }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < equipamentosLivres.length}
                      checked={equipamentosLivres.length > 0 && selectedIds.size === equipamentosLivres.length}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Equipamento</TableCell>
                  <TableCell>N. Série</TableCell>
                  <TableCell>MAC / GPON</TableCell>
                  <TableCell>Modalidade</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {equipamentosLivres.map((eq) => {
                  const isSelected = selectedIds.has(eq.id);
                  return (
                    <TableRow key={eq.id} hover onClick={() => handleToggle(eq.id)} sx={{ cursor: 'pointer', bgcolor: isSelected ? alpha(primario, 0.06) : undefined, '&:hover': { bgcolor: alpha(primario, 0.04) } }}>
                      <TableCell padding="checkbox"><Checkbox checked={isSelected} size="small" /></TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={eq.modelo?.imagem ? equipmentService.getModelImageUrl(eq.modelo.imagem) : undefined} sx={{ width: 28, height: 28 }}>
                            <RouterIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>{eq.modelo?.marca?.titulo} {eq.modelo?.titulo}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{equipmentService.getTipoLabel(eq.modelo?.tipo || '')}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{eq.numero_serie}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {eq.mac_address && <div>MAC: {eq.mac_address}</div>}
                        {eq.gpon_sn && <div>GPON: {eq.gpon_sn}</div>}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Chip label={equipmentService.getModalidadeLabel(eq.modalidade)} size="small" color={equipmentService.getModalidadeColor(eq.modalidade)} sx={{ height: 20, fontSize: '0.7rem' }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || selectedIds.size === 0 || !funcionarioId}
          startIcon={submitting ? <CircularProgress size={16} /> : <ExitToAppIcon />}
        >
          {submitting ? 'Lançando...' : `Lançar Saída (${selectedIds.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LancarSaidaModal;
